import dayjs from "dayjs";
import { isNil } from "lodash-es";
import { DUE_DATE_RULE } from "./enums";
import { DUE_DATE_RULE_DISPLAY } from "./displays";

/**
 * Tipagem de um formatter de valores numéricos
 */
export type NumberFormatter = (v?: number | string | null) => string;

/**
 * Formata um valor para o formato monetário brasileiro (R$)
 * @param money Valor numérico ou string a ser formatado
 * @returns String formatada como moeda brasileira
 */
export const formatMoney = (money?: number | string | null) => {
  if (isNil(money)) return "";
  return Number(money).toLocaleString("pt-br", {
    style: "currency",
    currency: "BRL",
  });
};

/**
 * Formata um valor para o formato percentual
 * @param value Valor numérico a ser formatado como percentual
 * @param options Opções adicionais de formatação numérica
 * @returns String formatada como percentual
 */
export const formatToPercent = (
  value?: number | null,
  options: Intl.NumberFormatOptions = {},
) => {
  if (!value) return "";

  const percentOptions = {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    ...options,
  };

  if (value > 1) {
    const formattedValue = (value / 100).toLocaleString("pt-br", {
      ...percentOptions,
      style: "percent",
    });
    return formattedValue;
  } else {
    const formattedValue = value.toLocaleString("pt-br", {
      ...percentOptions,
    });
    return `${formattedValue}%`;
  }
};

export const formatCurrency = (value?: number | string) => {
  if (!value) return "_";
  return `R$ ${Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
  })}`;
};

export const parseMoney = (value: string | number): number => {
  if (typeof value === "number") return value;
  return Number(
    value
      .replace(/\./g, "")
      .replace(",", ".")
      .replace(/[^\d.-]/g, ""),
  );
};

/**
 * Formata um valor decimal para formato percentual (multiplicado por 100)
 * @param value Valor decimal a ser convertido para percentual
 * @returns Número formatado como percentual (com o símbolo %)
 */
export const formatInterestLabel = (v?: string | number | null) =>
  formatToPercent(Number(v) * 100);

/**
 * Formata um valor decimal para formato percentual (multiplicado por 100)
 * @param value Valor decimal a ser convertido para percentual
 * @returns Número formatado como percentual (sem o símbolo %)
 */
export const formatTax = (value?: number) => {
  if (!value) return 0;

  const val = (value * 100).toFixed(2);

  return Number(val);
};

/**
 * Remove caracteres não numéricos de uma string
 * @param value String contendo números e possivelmente outros caracteres
 * @returns String contendo apenas os dígitos numéricos
 */
export const formatToNumberStr = (value?: string | null) => {
  return value?.replace(/\D/g, "");
};

/**
 * Converte uma string para número, removendo caracteres não numéricos
 * @param value String a ser convertida para número
 * @returns Valor numérico ou 0 se a string for vazia
 */
export const formatToNumber = (value: string) => {
  return value ? Number(formatToNumberStr(value)) : 0;
};

/**
 * Formata uma data para o formato especificado
 * @param date Data a ser formatada
 * @param formatStr String de formato (padrão dayjs)
 * @returns String com a data formatada
 */
export const formatDate = (
  date: dayjs.ConfigType,
  formatStr: string = "DD/MM/YYYY",
) => {
  if (!date) return "";
  return dayjs(date).format(formatStr);
};

/**
 * Formata um CEP para o padrão brasileiro (XXXXX-XXX)
 * @param cep Valor do CEP (string ou número)
 * @returns CEP formatado como string
 */
export const formatCEP = (cep?: string | number | null) => {
  if (!cep) return "";

  return cep
    .toString()
    .replace(/\D/g, "")
    .replace(/^(\d{5})(\d)/, "$1-$2");
};

/**
 * Formata um número de telefone para o padrão brasileiro
 * @param phone Número de telefone (string ou número)
 * @returns Telefone formatado como (XX) XXXXX-XXXX
 */
export const formatPhone = (phone?: string | number | null) => {
  if (!phone) return "";

  return phone
    .toString()
    .replace(/\D/g, "")
    .replace(/^(\d{2})(\d{4,5})(\d{4})$/, "($1) $2-$3");
};

/**
 * Formata um CPF para o padrão brasileiro (XXX.XXX.XXX-XX)
 * @param cpf String contendo os dígitos do CPF
 * @returns CPF formatado como string
 */
export const formatCpf = (cpf?: string) => {
  return cpf?.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") || "";
};

/**
 * Formata um CNPJ (number) para o padrão brasileiro (00.000.000/0000-00)
 * @param cnpj Número do CNPJ (deve ter até 14 dígitos)
 * @returns CNPJ formatado como string
 */
export const formatCnpj = (cnpj: number): string => {
  const cnpjStr = cnpj.toString().padStart(14, "0");

  return cnpjStr.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
};

/**
 * Formata um número de celular brasileiro com DDD (ex: (11) 91234-5678)
 * @param phone Número de celular (string ou número)
 * @returns Celular formatado como (XX) 9XXXX-XXXX
 */
export const formatMobilePhone = (phone?: string | number | null) => {
  if (!phone) return "";

  return phone
    .toString()
    .replace(/\D/g, "")
    .replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3");
};

/**
 * Cria função utilitária para formatações condicionais de texto no singlar/plural.
 *
 * @example
 * ```ts
 * const formatPersonAmount = createAmountFormatter('person', 'people')
 * const zeroPeople = formatPersonAmount(0); // "0 people"
 * const onePerson = formatPersonAmount(1); // "1 person"
 * const twoPeople = formatPersonAmount(2); // "2 people"
 * ```
 */
export const createAmountFormatter =
  <S extends string, P extends string>(singular: S, plural: P) =>
  (value?: number | string | null) => {
    if (value == null) return "";
    const _value = parseInt(value.toString());
    return `${_value} ${_value === 1 ? singular : plural}` as const;
  };

/**
 * Formata uma quantidade de meses como texto (singular/plural)
 * @param months Quantidade de meses
 * @returns String formatada com plural ou singular
 */
export const formatMonthAmount = createAmountFormatter("mês", "meses");

/**
 * Formata uma quantidade de dias como texto (singular/plural)
 * @param days Quantidade de dias
 * @returns String formatada com plural ou singular
 */
export const formatDaysAmount = createAmountFormatter("dia", "dias");

/**
 * Extrai o domínio (origin) de uma URL
 * @param url String contendo uma URL
 * @returns String com o domínio (protocol + hostname)
 */
export const formatOriginUrl = (url?: string) => {
  if (!url) return "";
  if (url.startsWith("http")) {
    return new URL(url).origin;
  } else {
    return new URL(`${location.protocol}//${url}`).origin;
  }
};

/**
 * Converte uma string de data para um objeto dayjs
 * @param date String de data (formatos ISO ou DD/MM/YYYY)
 * @returns Objeto dayjs ou null se a data for inválida
 */
export const formatISODate = (date?: string): dayjs.Dayjs | null => {
  const _date = date?.trim();
  if (_date) {
    if (isNaN(Date.parse(_date))) {
      const [day, month, year] = _date.split("/");
      return dayjs(`${year}-${month}-${day}`);
    }
    return dayjs(_date);
  }
  return null;
};

/**
 * Calcula a idade com base na data de nascimento
 * @param dateOfBirth Data de nascimento
 * @returns Idade em anos
 */
export const calculateAge = (dateOfBirth?: dayjs.ConfigType): number => {
  if (!dateOfBirth) {
    return 0;
  }

  const today = dayjs();
  const age = today.diff(dateOfBirth, "years");

  return age;
};

/**
 * Cria uma coluna formatada para valores monetários
 * @param title Título da coluna
 * @param dataIndex Nome da propriedade no objeto de dados
 * @returns Definição da coluna para o componente Table do Ant Design
 */
export const createMoneyColumn = <T extends object>(
  title: string,
  dataIndex: keyof T,
) => ({
  title,
  dataIndex,
  render: (value: number) => formatMoney(value),
});

/**
 * Cria uma coluna formatada para valores percentuais
 * @param title Título da coluna
 * @param dataIndex Nome da propriedade no objeto de dados
 * @returns Definição da coluna para o componente Table do Ant Design
 */
export const createPercentColumn = <T extends object>(
  title: string,
  dataIndex: keyof T,
) => ({
  title,
  dataIndex,
  render: (value: number) => value + "%",
});

/**
 * Cria uma coluna formatada com um renderizador personalizado
 * @param title Título da coluna
 * @param dataIndex Nome da propriedade no objeto de dados
 * @param renderer Função para renderizar o valor da célula
 * @returns Definição da coluna para o componente Table do Ant Design
 */
export const createFormattedColumn = <T extends object, V>(
  title: string,
  dataIndex: keyof T,
  renderer: (value: V) => React.ReactNode,
) => ({
  title,
  dataIndex,
  render: renderer,
});

export const formatDueDateRule = (rule?: string | DUE_DATE_RULE | null) => {
  const due_date_rules = Object.keys(DUE_DATE_RULE);
  if (!rule || !due_date_rules.includes(rule)) return rule ?? "-";
  return DUE_DATE_RULE_DISPLAY[rule as DUE_DATE_RULE];
};

export const formatAlternativeMoney = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
